import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import Typography from '@material-ui/core/Typography';
import Colors from '../utils/colors';
import { ErrorContext } from '../utils/constants';
import apiFetch from '../utils/apiUtils';

const useStyles = makeStyles(() => ({
    check: {
        color: Colors.green,
        margin: '0 0.5rem',
        verticalAlign: 'middle'
    },
    inline: {
        margin: '0 2rem',
    },
    btnDiv: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '5rem',
        paddingBottom: '5rem',
    },
    smallBtnDiv: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '2rem',
        paddingBottom: '2rem',
    },
    sendApprovalBtn: {
        "&:hover": {
            backgroundColor: Colors.brightBlue,
            color: Colors.indigoBlue
          },
        backgroundColor: Colors.darkBlue,
        minWidth: '225px'
    },
    approvalSentBtn: {
        backgroundColor: Colors.green,
        minWidth: '225px'
    },
    approvalBtn: {
        backgroundColor: Colors.darkBlue,
        "&:hover": {
            backgroundColor: Colors.brightBlue,
            color: Colors.indigoBlue
          },
        minWidth: '225px'
    },
    reviseBtn: {
        backgroundColor: Colors.darkBlue,
        "&:hover": {
            backgroundColor: Colors.brightBlue,
            color: Colors.indigoBlue
          },
        minWidth: '225px'
    }
}));

export function RequestApprovalButton({ approvalUrl, onSuccess, padding, buttonText }) {
    let { triggerError } = useContext(ErrorContext);
    let classes = useStyles();
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    const internalOnSuccess = () => {
        setIsSubmitting(false);
        onSuccess && onSuccess();
    };

    const submitForApproval = () => { 
        setIsSubmitting(true);

        apiFetch(
            approvalUrl,
            'POST',
            JSON.stringify({}),
            internalOnSuccess,
            (err) => {
                setIsSubmitting(false);
                if (err.status === 409) {
                    triggerError({
                        title: 'Approval already requested',
                        description: `Approval has already been requested for this initiative.`,
                        onRetry: () => window.location.reload()
                    });
                } else {
                    triggerError({
                        title: 'Communication Error',
                        description: 'We were unable to submit your initiative for approval. Please try again.',
                        onRetry: submitForApproval
                    });
                }
            }
        );
    };

    return (
        <div className={padding === 'small' ? classes.smallBtnDiv : classes.btnDiv}>
            <Button
                className={classes.sendApprovalBtn}
                variant='contained'
                color='primary'
                disabled={isSubmitting}
                onClick={submitForApproval}
            >
               {buttonText || 'SUBMIT FOR VALIDATION'}
            </Button>
        </div>
    );
}

export function ApprovalRequestedButton({ padding }) {
    let classes = useStyles();
    return (
        <div className={padding === 'small' ? classes.smallBtnDiv : classes.btnDiv}>
            <Button
                className={classes.approvalSentBtn}
                variant='contained'
                color='primary'
            >
                APPROVAL REQUESTED
            </Button>
        </div>
    )
}

export function ApproveOrReviseButtons({ approvalUrl, onSuccess, approveText, reviseText, padding }) {
    let { triggerError } = useContext(ErrorContext);
    let classes = useStyles();
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    const internalOnSuccess = () => {
        setIsSubmitting(false);
        onSuccess && onSuccess();
    };

    const submitApproval = (approved) => {        
        setIsSubmitting(true);

        apiFetch(
            approvalUrl,
            'POST',
            JSON.stringify({ approved }),
            internalOnSuccess,
            (err) => {
                setIsSubmitting(false);
                if (err.status === 409) {
                    triggerError({
                        title: approved ? 'Initiative already approved' : 'Revision already requested',
                        description: `This initiative has already ${approved ? 'been approved' : 'had revision requested' }.`,
                        onRetry: () => window.location.reload()
                    });
                } else {
                    triggerError({
                        title: 'Communication Error',
                        description: `We were unable to submit your ${approved ? 'approval' : 'request for revision'}. Please try again.`,
                        onRetry: submitApproval
                    });
                }
            }
        );
    };

    return (
        <div className={padding === 'small' ? classes.smallBtnDiv : classes.btnDiv}>
            <Button
                className={`${classes.inline} ${classes.approvalBtn}`}
                onClick={() => submitApproval(true)}
                variant='contained'
                color='primary'
                disabled={isSubmitting}
            >
                { approveText ? approveText : 'APPROVE' }
            </Button>
            <Button
                className={`${classes.inline} ${classes.reviseBtn}`}
                onClick={() => submitApproval(false)}
                variant='contained'
                color='primary'
                disabled={isSubmitting}
            >
                { reviseText ? reviseText : 'REJECT' }
            </Button>
        </div>
    );
}

export function ApprovalGranted({ approvalType, approverNames, missingApprovers, padding }) {
    let classes = useStyles();
    const stretchText = (names) => {
        if (names.length === 1) {
            return names[0];
        } if (names.length > 1) {
            return names.join(' and ')
        }
    };

    return (
        <div className={padding === 'small' ? classes.smallBtnDiv : classes.btnDiv}>
        <Typography>
            <CheckCircleOutlineIcon className={classes.check} />
            {`${approvalType ? approvalType + ' Validated' : 'Validated'} by ${stretchText(approverNames)}.`}
            {(missingApprovers && missingApprovers.length > 0) &&
                ` Waiting on ${stretchText(missingApprovers)} validation.`
            }
        </Typography>
        </div>
    );
}
